/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { FC } from "react";
import { IEditor } from "../IEditor";

/**
 * Dialog component properties
 */
export interface IDialogComponentProps<TDialogInstance extends Dialog<unknown, unknown>> {
	dialog: TDialogInstance;
}

/**
 * Dialog component type
 */
export type TDialogComponent<TDialogInstance extends Dialog<unknown, unknown>> = FC<
	IDialogComponentProps<TDialogInstance>
>;

/**
 * Base dialog class
 * All dialogs must extend this class.
 */
export abstract class Dialog<TOpenParams = unknown, TCloseResult = unknown> {
	/** Dependency container */
	protected readonly dc: IEditor;

	/** Dialog ID */
	protected readonly id: string;

	/** Open params */
	protected readonly params: TOpenParams;

	/** If the dialog was closed */
	private isClosed: boolean;

	/** Last close result */
	private closeResult: TCloseResult = null;

	/** Function called when the dialog is closed - internally bound to resolve promise */
	private closeResolveCallback: (result: TCloseResult) => void = null;

	/** Promise resolved when the dialog is closed */
	private readonly closePromise: Promise<TCloseResult> = null;

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: TOpenParams) {
		this.dc = dc;
		this.id = id;
		this.params = params;

		this.closePromise = new Promise((resolve) => {
			this.closeResolveCallback = resolve;
		});

		this.init();
	}

	/**
	 * Returns dialog ID
	 */
	public getId(): string {
		return this.id;
	}

	/**
	 * Returns open params
	 */
	public getParams(): TOpenParams {
		return this.params;
	}

	/**
	 * Initializes the dialog instance
	 */
	protected init(): void {
		// Should be overriden
	}

	/**
	 * Returns promise that is resolved when the dialog is closed
	 * @returns
	 */
	public getClosePromise(): Promise<TCloseResult> {
		return this.closePromise;
	}

	/**
	 * Closes the dialog
	 * To prevent close for some reason override this method and call parent's one when you want to close the dialog.
	 *
	 * @param result Close result data
	 */
	public close(result: TCloseResult): void {
		if (this.isClosed) {
			return;
		}

		this.isClosed = true;
		this.closeResult = result;
		this.closeResolveCallback(result);
	}

	/**
	 * Returns if the dialog has been closed
	 */
	public wasClosed(): boolean {
		return this.isClosed;
	}

	/**
	 * Returns the close result (if dialog was closed)
	 */
	public getCloseResult(): TCloseResult {
		return this.closeResult;
	}

	/**
	 * Disposes dialog
	 */
	public dispose(): void {
		// Should be overriden
	}

	/**
	 * Returns react component to render dialog contents
	 */
	public getComponent(): TDialogComponent<Dialog<TOpenParams>> {
		return null;
	}
}

/**
 * Dialog constructor
 */
export interface IDialogConstructor<TDialog extends Dialog> {
	/**
	 * View constructor
	 */
	new (dc: IEditor, id: string, params: TGetDialogOpenParams<TDialog>): TDialog;
}

/**
 * Type helper to return dialog open params
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TGetDialogOpenParams<TDialog> = TDialog extends Dialog<infer A, infer B> ? A : never;

/**
 * Type helper to return dialog close result
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TGetDialogCloseResult<TDialog> = TDialog extends Dialog<infer A, infer B> ? B : never;
